import React, { useState } from 'react';
import { SlidersHorizontal, Link } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from "./sheet";
import { Button } from "./button";
import { Label } from "./label";
import { Slider } from "./slider";
import { Checkbox } from "./checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";
import { CSS4_COLORS } from './mplcolors';
import { Bike, Building2, Trees, Mountain, Waves, Droplets, Car, LandPlot } from 'lucide-react';
import { Input } from "./input";

const ACTIVITY_TYPES = ['Run', 'Ride', 'Hike', 'Walk', 'Swim', 'TrailRun', 'VirtualRide'];

const ColorSettingsPopup = ({
  settings,
  onColorSettingChange,
  onActivityTypeSettingChange,
  triggerButton,
  syncOceansWater,
  onSyncToggle,
  activitySource,
}) => {
  const categories = ['activities', 'land', 'buildings', 'streets', 'forests', 'naturalareas', 'oceans', 'water'];
  const lineWidths = ["very thin", "thin", "normal", "thick", "very thick", "custom"];
  const streetType = ["roads", "all"];
  const streetType_reduced = ["roads"];
  const [sync, setSync] = useState(false || syncOceansWater);
  const [activityTab, setActivityTab] = useState(ACTIVITY_TYPES[0]);
  const [customLineWidthValue, setCustomLineWidthValue] = useState({
    activities: typeof settings.activities.lineWidth === 'number' ? settings.activities.lineWidth : 1,
  });

  const categoryIcons = {
    activities: Bike,
    land: LandPlot,
    buildings: Building2,
    forests: Trees,
    naturalareas: Mountain,
    oceans: Waves,
    water: Droplets,
    streets: Car,
  };

  const handleCustomColorClick = (category) => {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = settings[category].color;
    input.addEventListener('change', (e) => {
      onColorSettingChange(category, 'color', e.target.value);
      if (sync && category === 'oceans') {
        onColorSettingChange('water', 'color', e.target.value);
      }
    });
    input.click();
  };

  const getDisplayValue = (color) => {
    if (color && color.startsWith('#')) return `custom: ${color}`;
    return color;
  };

  const handleSliderChange = (category, value) => {
    onColorSettingChange(category, 'alpha', value[0] / 100);
    if (sync && category === 'oceans') {
      onColorSettingChange('water', 'alpha', value[0] / 100);
    }
  };

  const handleOpacityInputChange = (category, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onColorSettingChange(category, 'alpha', numValue / 100);
      if (sync && category === 'oceans') {
        onColorSettingChange('water', 'alpha', numValue / 100);
      }
    }
  };

  const handleColorChange = (category, color) => {
    onColorSettingChange(category, 'color', color);
    if (sync && category === 'oceans') {
      onColorSettingChange('water', 'color', color);
    }
  };

  // Open a native color picker that writes back through an arbitrary setter —
  // used for per-activity-type tabs so the "custom" option persists to byType.
  const openCustomColorPicker = (initialColor, setColor) => {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = initialColor && initialColor.startsWith('#') ? initialColor : '#ff0000';
    input.addEventListener('change', (e) => setColor(e.target.value));
    input.click();
  };

  // Render the shared color / opacity / line-width controls from a resolved
  // scope and a single write function. Used for both the common activities
  // settings and per-type overrides.
  const renderActivityStyleControls = ({ color, alpha, lineWidth, onChange, lwKey }) => {
    const isCustomLineWidth = typeof lineWidth === 'number';
    return (
      <>
        <div className="space-y-2">
          <Label>Color</Label>
          <Select
            value={color}
            onValueChange={(value) => {
              if (value === 'custom') {
                openCustomColorPicker(color, (c) => onChange('color', c));
                return;
              }
              onChange('color', value);
            }}
          >
            <SelectTrigger className="bg-white border border-none rounded-xl">
              <SelectValue>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 rounded-full" style={{ backgroundColor: color }} />
                  {getDisplayValue(color)}
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div className="max-h-96 overflow-y-auto">
                <SelectItem value="custom">
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 mr-2 rounded-full"
                      style={{ background: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)' }}
                    />
                    custom
                  </div>
                </SelectItem>
                {Object.keys(CSS4_COLORS).map((c) => (
                  <SelectItem key={c} value={c}>
                    <div className="flex items-center">
                      <div className="w-4 h-4 mr-2 rounded-full" style={{ backgroundColor: c }} />
                      {c}
                    </div>
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 mt-4">
          <Label>Opacity ({Math.round(alpha * 100)}%)</Label>
          <div className="flex items-center gap-2">
            <div className="flex-grow">
              <Slider
                value={[alpha * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={(value) => onChange('alpha', value[0] / 100)}
                className="border-2 border-white rounded-xl"
              />
            </div>
            <Input
              type="number"
              min={0}
              max={100}
              value={Math.round(alpha * 100)}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n) && n >= 0 && n <= 100) onChange('alpha', n / 100);
              }}
              className="w-16 h-8 text-center"
            />
          </div>
        </div>

        <div className="space-y-2 mt-4">
          <Label>Line Width</Label>
          <Select
            value={isCustomLineWidth ? 'custom' : lineWidth}
            onValueChange={(value) => {
              if (value === 'custom') {
                const seed = customLineWidthValue[lwKey]
                  || (typeof settings.activities.lineWidth === 'number' ? settings.activities.lineWidth : 1);
                setCustomLineWidthValue(prev => ({ ...prev, [lwKey]: seed }));
                onChange('lineWidth', seed);
              } else {
                onChange('lineWidth', value);
              }
            }}
          >
            <SelectTrigger className="bg-white border border-none rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {lineWidths.map((w) => (
                <SelectItem key={w} value={w}>{w}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isCustomLineWidth && (
            <div className="mt-2">
              <div className="flex items-center gap-2 mt-2">
                <div className="flex-grow">
                  <Slider
                    value={[lineWidth]}
                    min={0.1}
                    max={10}
                    step={0.1}
                    onValueChange={(value) => {
                      const n = parseFloat(value[0]);
                      setCustomLineWidthValue(prev => ({ ...prev, [lwKey]: n }));
                      onChange('lineWidth', n);
                    }}
                    className="border-2 border-white rounded-xl"
                  />
                </div>
                <Input
                  type="number"
                  min={0.1}
                  max={10}
                  step={0.1}
                  value={lineWidth}
                  onChange={(e) => {
                    const n = parseFloat(e.target.value);
                    if (!isNaN(n) && n >= 0.1 && n <= 10) {
                      setCustomLineWidthValue(prev => ({ ...prev, [lwKey]: n }));
                      onChange('lineWidth', n);
                    }
                  }}
                  className="w-16 h-8 text-center"
                />
              </div>
              <div className="text-xs text-gray-500 mt-1 text-center">Range: 0.1 - 10</div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderActivityBody = () => {
    const activities = settings.activities || {};
    const byTypeEnabled = !!activities.byTypeEnabled;

    const commonScope = {
      color: activities.color,
      alpha: activities.alpha,
      lineWidth: activities.lineWidth,
    };

    const commonOnChange = (setting, value) => onColorSettingChange('activities', setting, value);

    const canEditPerType = byTypeEnabled && typeof onActivityTypeSettingChange === 'function';
    const typeOverrides = activities.byType?.[activityTab] || {};
    const typeScope = {
      color: typeOverrides.color || activities.color,
      alpha: typeOverrides.alpha != null ? typeOverrides.alpha : activities.alpha,
      lineWidth: typeOverrides.lineWidth != null ? typeOverrides.lineWidth : activities.lineWidth,
    };
    const typeOnChange = (setting, value) => onActivityTypeSettingChange(activityTab, setting, value);

    return (
      <>
        {typeof onActivityTypeSettingChange === 'function' && (
          <div className="flex items-center justify-between mb-3">
            <Label htmlFor="byTypeEnabled" className="text-sm cursor-pointer">
              Choose different colors for activities
            </Label>
            <Checkbox
              id="byTypeEnabled"
              checked={byTypeEnabled}
              onCheckedChange={(checked) => onColorSettingChange('activities', 'byTypeEnabled', !!checked)}
              className="h-5 w-5 rounded-lg border-black"
            />
          </div>
        )}

        {canEditPerType && (
          <div className="-mx-2 px-2 border-b border-gray-300 mb-3 overflow-x-auto">
            <div className="flex gap-1">
              {ACTIVITY_TYPES.map((type) => {
                const isActive = activityTab === type;
                const hasOverride = !!activities.byType?.[type];
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setActivityTab(type)}
                    className={`px-3 py-1.5 text-xs whitespace-nowrap rounded-t-lg border border-b-0 ${
                      isActive
                        ? 'bg-white font-semibold border-gray-300'
                        : 'bg-transparent border-transparent text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {type}
                    {hasOverride && <span className="ml-1 text-[10px] text-blue-600">●</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {canEditPerType ? (
          <>
            {renderActivityStyleControls({
              ...typeScope,
              onChange: typeOnChange,
              lwKey: `activities:${activityTab}`,
            })}
            {activities.byType?.[activityTab] && (
              <div className="mt-3 text-right">
                <button
                  type="button"
                  onClick={() => onActivityTypeSettingChange(activityTab, '__reset__', null)}
                  className="text-xs text-gray-500 hover:text-gray-800 underline"
                >
                  Reset {activityTab} to common
                </button>
              </div>
            )}
          </>
        ) : (
          renderActivityStyleControls({
            ...commonScope,
            onChange: commonOnChange,
            lwKey: 'activities',
          })
        )}
      </>
    );
  };

  const renderContent = () => (
    <div className="space-y-4 py-4 mr-4">
      {categories.map((category) => {
        const isWaterCategory = category === 'water';
        const isWaterSynced = sync && isWaterCategory;
        const isActivitiesDisabled = category === 'activities' && activitySource === 'none';

        return (
          <div
            key={category}
            className={`space-y-2 shadow-xl rounded-xl p-2 pb-4 bg-gray-200 relative ${isWaterSynced ? 'opacity-70' : ''}`}
          >
            {isWaterSynced && (
              <div className="absolute inset-0 bg-gray-300 bg-opacity-50 flex items-center justify-center rounded-xl z-10">
                <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-2xl shadow text-center">
                  <div className="flex items-center gap-2 justify-center">
                    <Link className="h-4 w-4" />
                    <span className="font-medium">Synced with oceans</span>
                  </div>
                  <p className="text-xs mt-1">Settings controlled by oceans category</p>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <Checkbox
                checked={settings[category].active}
                disabled={
                  (category === 'buildings' && settings.km2 > 15000) ||
                  (category === 'streets' && settings.km2 > 15000) ||
                  isActivitiesDisabled ||
                  isWaterSynced
                }
                onCheckedChange={(checked) => {
                  onColorSettingChange(category, 'active', checked);
                  if (sync && category === 'oceans') {
                    onColorSettingChange('water', 'active', checked);
                  }
                }}
                className="h-6 w-6 border-black rounded-xl"
              />
              <div className="flex items-center gap-2">
                <Label className="text-lg capitalize">{category}</Label>
                {React.createElement(categoryIcons[category], { className: "h-4 w-4" })}
              </div>
              <div className="flex items-center gap-3">
                {settings[category].active && !isWaterSynced && (
                  <div
                    className="w-6 h-6 rounded border border-gray-200"
                    style={{
                      backgroundColor: settings[category].color,
                      opacity: settings[category].alpha
                    }}
                    title="Color preview"
                  />
                )}
              </div>
            </div>

            {isActivitiesDisabled && (
              <div className="text-xs text-gray-500 text-center">
                Select an activity source to enable activities.
              </div>
            )}

            {settings[category].active && !isActivitiesDisabled && (
              <div className="">
                {category === 'activities' ? (
                  renderActivityBody()
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Color</Label>
                      <Select
                        value={settings[category].color}
                        onValueChange={(value) => {
                          if (value === 'custom') {
                            handleCustomColorClick(category);
                            return;
                          }
                          handleColorChange(category, value);
                        }}
                      >
                        <SelectTrigger className="bg-white border border-none rounded-xl">
                          <SelectValue>
                            <div className="flex items-center">
                              <div
                                className="w-4 h-4 mr-2 rounded-full"
                                style={{ backgroundColor: settings[category].color }}
                              />
                              {getDisplayValue(settings[category].color)}
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <div className="max-h-96 overflow-y-auto">
                            <SelectItem value="custom">
                              <div className="flex items-center">
                                <div
                                  className="w-4 h-4 mr-2 rounded-full"
                                  style={{ background: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)' }}
                                />
                                custom
                              </div>
                            </SelectItem>
                            {Object.keys(CSS4_COLORS).map((color) => (
                              <SelectItem key={color} value={color}>
                                <div className="flex items-center">
                                  <div
                                    className="w-4 h-4 mr-2 rounded-full"
                                    style={{ backgroundColor: color }}
                                  />
                                  {color}
                                </div>
                              </SelectItem>
                            ))}
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label>Opacity ({Math.round(settings[category].alpha * 100)}%)</Label>
                      <div className="flex items-center gap-2">
                        <div className="flex-grow">
                          <Slider
                            value={[settings[category].alpha * 100]}
                            min={0}
                            max={100}
                            step={1}
                            onValueChange={(value) => handleSliderChange(category, value)}
                            className="border-2 border-white rounded-xl"
                          />
                        </div>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={Math.round(settings[category].alpha * 100)}
                          onChange={(e) => handleOpacityInputChange(category, e.target.value)}
                          className="w-16 h-8 text-center"
                        />
                      </div>
                    </div>
                    {category === 'streets' && (
                      <div className="space-y-2 mt-4">
                        <Label>Street details</Label>
                        <Select
                          value={settings.streets.details}
                          onValueChange={(value) =>
                            onColorSettingChange('streets', 'details', value)
                          }
                        >
                          <SelectTrigger className="bg-white border border-none rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {settings.km2 <= 4000
                              ? streetType.map((details) => (
                                <SelectItem key={details} value={details}>
                                  {details}
                                </SelectItem>
                              ))
                              : streetType_reduced.map((details) => (
                                <SelectItem key={details} value={details}>
                                  {details}
                                </SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {category === 'streets' && settings.km2 > 4000 ? (
                      <div className="text-red-500 text-center text-sm">Only main roads can be chosen for areas bigger than 4000 km²</div>
                    ) : null}
                  </>
                )}

                {category === 'oceans' && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <Checkbox
                      checked={sync}
                      id="syncWaterCheckbox"
                      onCheckedChange={(checked) => {
                        if (onSyncToggle) onSyncToggle(checked);
                        setSync(checked);
                        if (checked) {
                          onColorSettingChange('water', 'color', settings.oceans.color);
                          onColorSettingChange('water', 'alpha', settings.oceans.alpha);
                          onColorSettingChange('water', 'active', settings.oceans.active);
                        }
                      }}
                      className="h-4 w-4 rounded-xl border-black"
                    />
                    <Label htmlFor="syncWaterCheckbox" className="text-sm flex items-center gap-1">
                      Sync water <Link className="h-3 w-3" />
                    </Label>
                  </div>
                )}
                {category === 'oceans' && sync && (
                  <div className="mt-2 text-blue-600 text-xs text-center">
                    Water uses the same color and opacity as oceans
                  </div>
                )}
              </div>
            )}
            {category === 'buildings' && settings.km2 > 15000 && (
              <div className="text-red-500 text-sm">Buildings disabled for areas bigger than 15000 km²</div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (triggerButton) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button
            size="sm"
            className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Customize
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full overflow-y-auto">
          <SheetHeader>
            {/* <SheetTitle>Color Settings</SheetTitle> */}
          </SheetHeader>
          {renderContent()}
        </SheetContent>
      </Sheet>
    );
  }

  return renderContent();
};

export default ColorSettingsPopup;
